if (!customElements.get('imh-collection-rail')) {
  class ImhCollectionRail extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.scroller = this.querySelector('[data-rail]');
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      if (!this.scroller) return;

      this.autoplay = this.hasAttribute('autoplay');
      this.imageMs = (Number(this.getAttribute('image-interval')) || 2) * 1000;
      this.scrollMs = (Number(this.getAttribute('scroll-interval')) || 4) * 1000;
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollPaused = false;
      this.dragging = false;
      this.moved = false;
      this.details = this.closest('details');

      this.onToggle = () => {
        if (this.details?.open) {
          requestAnimationFrame(() => this.update());
          this.start();
        } else {
          this.stop();
        }
      };

      this.onResize = () => this.update();
      this.prev?.addEventListener('click', () => {
        this.nudge();
        this.move(-1);
      });
      this.next?.addEventListener('click', () => {
        this.nudge();
        this.move(1);
      });

      this.scroller.addEventListener('scroll', this.onResize, { passive: true });
      this.scroller.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.nudge();
          this.move(1);
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.nudge();
          this.move(-1);
        }
      });

      this.addEventListener('pointerenter', () => {
        this.scrollPaused = true;
      });
      this.addEventListener('pointerleave', () => {
        this.scrollPaused = false;
      });
      this.addEventListener('focusin', () => {
        this.scrollPaused = true;
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.scrollPaused = false;
      });

      this.dragStartX = 0;
      this.dragStartScroll = 0;
      this.scroller.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        this.dragging = true;
        this.moved = false;
        this.scrollPaused = true;
        this.dragStartX = event.clientX;
        this.dragStartScroll = this.scroller.scrollLeft;
        this.scroller.classList.add('is-dragging');
        this.scroller.setPointerCapture(event.pointerId);
      });
      this.scroller.addEventListener('pointermove', (event) => {
        if (!this.dragging) return;
        const delta = event.clientX - this.dragStartX;
        if (Math.abs(delta) > 6) this.moved = true;
        this.scroller.scrollLeft = this.dragStartScroll - delta;
      });
      const endDrag = () => {
        this.dragging = false;
        this.scroller.classList.remove('is-dragging');
      };
      this.scroller.addEventListener('pointerup', endDrag);
      this.scroller.addEventListener('pointercancel', endDrag);
      this.scroller.addEventListener('click', (event) => {
        if (!this.moved) return;
        event.preventDefault();
        event.stopPropagation();
        this.moved = false;
      }, true);

      this.prepareSlides();
      this.details?.addEventListener('toggle', this.onToggle);
      window.addEventListener('resize', this.onResize, { passive: true });

      if (this.details?.open) this.onToggle();
    }

    disconnectedCallback() {
      this.stop();
      this.details?.removeEventListener('toggle', this.onToggle);
      window.removeEventListener('resize', this.onResize);
      this.ready = false;
    }

    prepareSlides() {
      this.querySelectorAll('.imh-rail__media').forEach((media, index) => {
        const images = [...media.querySelectorAll('.imh-rail__image')];
        if (!images.length) return;
        const start = index % images.length;
        images.forEach((image, imageIndex) => {
          image.classList.toggle('is-active', imageIndex === start);
        });
      });
    }

    start() {
      this.stop();
      if (this.reduce) return;

      this.imageTimer = window.setInterval(() => this.advanceImages(), this.imageMs);
      if (!this.autoplay) return;
      this.scrollTimer = window.setInterval(() => {
        if (this.scrollPaused || this.dragging) return;
        this.move(1);
      }, this.scrollMs);
    }

    stop() {
      window.clearInterval(this.imageTimer);
      window.clearInterval(this.scrollTimer);
    }

    nudge() {
      this.scrollPaused = true;
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => {
        if (!this.matches(':hover') && !this.contains(document.activeElement)) {
          this.scrollPaused = false;
        }
      }, this.scrollMs);
    }

    advanceImages() {
      this.querySelectorAll('.imh-rail__media').forEach((media) => {
        const images = [...media.querySelectorAll('.imh-rail__image')];
        if (images.length < 2) return;
        const current = Math.max(0, images.findIndex((image) => image.classList.contains('is-active')));
        images[current].classList.remove('is-active');
        images[(current + 1) % images.length].classList.add('is-active');
      });
    }

    step() {
      const item = this.scroller.querySelector('.imh-rail__item');
      if (!item) return Math.round(this.scroller.clientWidth * 0.7);
      const track = this.scroller.querySelector('.imh-rail__track');
      const gap = track ? parseFloat(getComputedStyle(track).columnGap) || 0 : 0;
      return item.getBoundingClientRect().width + gap;
    }

    move(direction) {
      const max = this.scroller.scrollWidth - this.scroller.clientWidth;
      if (max <= 8) return;

      if (direction > 0 && this.scroller.scrollLeft >= max - 4) {
        this.scroller.scrollTo({ left: 0, behavior: this.reduce ? 'auto' : 'smooth' });
        return;
      }

      if (direction < 0 && this.scroller.scrollLeft <= 4) {
        this.scroller.scrollTo({ left: max, behavior: this.reduce ? 'auto' : 'smooth' });
        return;
      }

      this.scroller.scrollBy({
        left: this.step() * direction,
        behavior: this.reduce ? 'auto' : 'smooth'
      });
    }

    update() {
      const max = this.scroller.scrollWidth - this.scroller.clientWidth;
      this.classList.toggle('can-scroll', max > 8);
    }
  }

  customElements.define('imh-collection-rail', ImhCollectionRail);
}
